import { DEFER_COLLAB_SEND } from '@atlaskit/editor-common/collab-defer-collab-send';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { resolveAgentAttribution } from '../../agent-attribution/resolveAgentAttribution';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { metaTransaction } from '../transforms';
import { restoreOriginalCell } from './cellKindConversionTransactions';

/**
 * Builds transaction for restoring original content before final commit.
 * This is Phase 1 of the two-dispatch finalize pattern.
 *
 * The restore transaction:
 * 1. Replaces the current AI streaming content with the original user content
 * 2. Marks itself with 'isAIRestoreTransaction' so track-changes can identify it
 * 3. Does NOT mark as final (isFinalStream: false)
 */
export function buildRestoreTransaction(state: EditorState): Transaction | null {
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	if (!streamingState) {
		return null;
	}

	const { startPos, userContentEndPos, originalUserContentSlice } = streamingState;

	if (startPos === undefined || userContentEndPos === undefined || !originalUserContentSlice) {
		return null;
	}

	const streamTimeStamp = Date.now();
	let tr = state.tr;

	try {
		if (streamingState.cellKindConversion?.hasApplied) {
			const restored = restoreOriginalCell(state);
			if (!restored) {
				return null;
			}
			tr = restored;
		} else if (originalUserContentSlice.content.size > 0) {
			tr = tr.replaceRange(startPos, userContentEndPos, originalUserContentSlice);
		} else {
			// If original was empty, we need to delete the AI content.
			// IMPORTANT: Use replaceRange with Slice.empty instead of delete()
			// because replaceRange properly handles block boundaries.
			// When a block node (e.g., heading) was inserted with openStart=0,
			// positions track content boundaries (inside the block), not node
			// boundaries. Using delete() would merge the block with adjacent content.
			// replaceRange with Slice.empty will delete the appropriate range
			// while preserving the structure of surrounding nodes.
			tr = tr.replaceRange(startPos, userContentEndPos, Slice.empty);
		}

		// Restore runs as part of finalize, so the hold is no longer wanted.
		tr = tr.setMeta(DEFER_COLLAB_SEND, false);
		// Part of the agent's finalize: unattributed it reaches NCS as a human step.
		const agentAttribution = fg('confluence_ncs_step_diffing_version_history')
			? resolveAgentAttribution({
					sessionAgentAttribution: aiSessionContextPluginKey.getState(state)?.agentAttribution,
					configItemKey: aiExperienceDecorationPluginKey.getState(state)?.configItem?.key,
				})
			: undefined;
		tr = metaTransaction({
			tr,
			isFinalStream: false,
			stackTime: streamTimeStamp,
			agentAttribution,
		});
		// Mark as restore transaction so track-changes knows this is part of finalize
		tr = tr.setMeta('isAIRestoreTransaction', true);
		// Don't add to history - the commit transaction will be the undo point
		tr = tr.setMeta('addToHistory', false);
	} catch {
		return null;
	}

	return tr;
}
